import { useState, useRef, useEffect } from "react";

import useOutsideClick from "../../../hooks/use-outside-click";
import useEventListener from "../../../hooks/use-event-listener";
import useScrollEndListener from "../../../hooks/use-scroll-end-listener";

const DropdownMultipleSelector = ({
  dimensionIndex,
  questionIndex,
  defaultAnswer,
  options,
  handleChange,
}) => {
  const answersObj = defaultAnswer ?? [];
  const selectedOptions = answersObj
    .map((answer, index) => ({ ...answer, index: index }))
    .filter((answer) => answer["value"] === true);
  const [dropdownIsVisible, setDropdownIsVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState("below");

  const onClickSelector = () => setDropdownIsVisible((dropdownIsVisible) => !dropdownIsVisible);
  const onClickOutsideDropdown = () =>
    setDropdownIsVisible((dropdownIsVisible) => {
      if (dropdownIsVisible) {
        return false;
      }
    });
  const onClickClearOptions = () => {
    for (let i = 0; i < selectedOptions.length; i++) {
      optionRefs.current[selectedOptions[i]["index"]].click();
    }
  };
  const onClickDeleteOption = (option) => optionRefs.current[option["index"]].click();

  // En el final de un scroll, determinará si el dropdown debe estar encima o debajo del selector.
  const onScrollWindowEnd = () => {
    setDropdownPosition((_) => {
      if (
        window.innerHeight - selectorRef.current.getBoundingClientRect().bottom <
        dropdownRef.current.clientHeight
      ) {
        return "above";
      } else {
        return "below";
      }
    });
  };

  // Esconderá el dropdown si se hace scroll en la página
  useEventListener("scroll", () =>
    setDropdownIsVisible((dropdownIsVisible) => {
      if (dropdownIsVisible) {
        return false;
      }
    })
  );
  useScrollEndListener(onScrollWindowEnd);

  const selectorRef = useRef();
  const collapseIconRef = useRef();
  const dropdownRef = useOutsideClick(onClickOutsideDropdown, [selectorRef, collapseIconRef]);
  const optionRefs = useRef([]);

  useEffect(() => {
    if (dropdownIsVisible) {
      dropdownRef.current.scrollTo({ top: 0 });
    }
  }, [dropdownIsVisible]);

  // Se asigna cada ref a las opciones del dropdown
  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, options.length);
  }, [options]);

  const isCheckedOption = (optionIndex) => {
    if (!answersObj) {
      return false;
    }

    return answersObj[optionIndex] && answersObj[optionIndex]["value"] ? true : false;
  };

  const renderSelectorOption = (option) => {
    return (
      <div
        key={`survey-question-field-dropdown-${dimensionIndex}_${questionIndex}-selected-option-${option["index"]}`}
        className="option"
      >
        <i className="close-icon fas fa-times mr-2" onClick={() => onClickDeleteOption(option)} />
        {option["name"]}
      </div>
    );
  };

  const renderDropdownOption = (option, optionIndex) => {
    return (
      <div
        key={`survey-question-field-dropdown-${dimensionIndex}_${questionIndex}-dropdown-option-${optionIndex}`}
        className="option"
      >
        <input
          id={`dropdown_${dimensionIndex}_${questionIndex}_${optionIndex}`}
          ref={(el) => (optionRefs.current[optionIndex] = el)}
          name={`dropdown_${dimensionIndex}_${questionIndex}`}
          type="checkbox"
          onChange={handleChange}
          checked={isCheckedOption(optionIndex)}
        />
        <label htmlFor={`dropdown_${dimensionIndex}_${questionIndex}_${optionIndex}`}>
          &ensp;{option}
        </label>
      </div>
    );
  };

  return (
    <div className="multiple-selector">
      <div
        className={`selector form-control ${dropdownIsVisible ? "multiple-dropdown-visible" : ""}`}
        onClick={onClickSelector}
        ref={selectorRef}
      >
        {selectedOptions.length > 0 ? (
          selectedOptions.map((option) => renderSelectorOption(option))
        ) : (
          <div className="placeholder-message">No has seleccionado ninguna opción</div>
        )}
      </div>
      {selectedOptions.length > 0 && (
        <>
          <i className="icon clear fas fa-times" onClick={onClickClearOptions} />
          <div className="separator" />
        </>
      )}
      <i
        className="icon collapse fas fa-chevron-down"
        ref={collapseIconRef}
        onClick={onClickSelector}
      />
      <div
        className={`multiple-dropdown ${
          dropdownIsVisible ? "visible" : ""
        } ${dropdownPosition}-selector`}
        ref={dropdownRef}
      >
        {options.map((option, optionIndex) => renderDropdownOption(option, optionIndex))}
      </div>
    </div>
  );
};

export default DropdownMultipleSelector;
